import useSuitesProjectsLanguage from '../useSuitesProjectsLanguage';
import React from 'react';
import { Plus, Search, Workflow, FlaskConical, X } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export default function SuiteAddTestsDialog({
  showAddTestModal,
  selectedSuite,
  setShowAddTestModal,
  getEffectiveSuiteType,
  testSearchQuery,
  setTestSearchQuery,
  testTypeFilter,
  setTestTypeFilter,
  filteredTests,
  handleAddTest
}) {
  const { t } = useSuitesProjectsLanguage();
  return (<>
      {/* Add Test Modal */}
      {showAddTestModal && selectedSuite && (
        <div className="lexa-suite-overlay fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="lexa-suite-dialog lexa-suite-dialog--add-tests w-full max-w-lg p-0 max-h-[80vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{t("Add Tests")}</h2>
                <Button variant="ghost" size="sm" aria-label={t("Close")} onClick={() => setShowAddTestModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Suite Type Info Banner */}
              {getEffectiveSuiteType() && (
                <div className={`mt-3 p-2 rounded-lg flex items-center text-sm ${
                  getEffectiveSuiteType() === 'api_testing'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-indigo-50 text-indigo-700'
                }`}>
                  {getEffectiveSuiteType() === 'api_testing' ? (
                    <>
                      <FlaskConical className="w-4 h-4 mr-2" />
                      {t("This is an API Testing suite - only API tests can be added")}
                    </>
                  ) : (
                    <>
                      <Workflow className="w-4 h-4 mr-2" />
                      {t("This is a Flow Testing suite - only Flow tests can be added")}
                    </>
                  )}
                </div>
              )}

              {/* Search and Filter */}
              <div className="lexa-suite-search-row flex space-x-2 mt-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    className="pl-9"
                    placeholder={t("Search tests...")}
                    value={testSearchQuery}
                    onChange={(e) => setTestSearchQuery(e.target.value)}
                  />
                </div>
                {/* Only show type filter for mixed/legacy suites */}
                {!getEffectiveSuiteType() && (
                  <select
                    className="px-3 py-2 border rounded-lg text-sm"
                    value={testTypeFilter}
                    onChange={(e) => setTestTypeFilter(e.target.value)}
                  >
                    <option value="all">{t("All")}</option>
                    <option value="api_test">API</option>
                    <option value="flow_test">{t("Flow")}</option>
                  </select>
                )}
              </div>
            </div>

            {/* Tests List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {filteredTests.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  {getEffectiveSuiteType() === 'api_testing'
                    ? t("No API tests available to add")
                    : getEffectiveSuiteType() === 'flow_testing'
                    ? t("No Flow tests available to add")
                    : t("No tests available to add")
                  }
                </div>
              ) : (
                filteredTests.map(test => (
                  <div
                    key={test.id}
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 group"
                  >
                    <div className={`p-1.5 rounded ${test.type === 'api_test' ? 'bg-green-100' : 'bg-indigo-100'} mr-3`}>
                      {test.type === 'api_test' ? (
                        <FlaskConical className="w-4 h-4 text-green-600" />
                      ) : (
                        <Workflow className="w-4 h-4 text-indigo-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{test.name}</p>
                      <p className="text-xs text-gray-500">
                        {test.type === 'api_test'
                          ? `${test.method} ${test.url?.substring(0, 35)}${test.url?.length > 35 ? '...' : ''}`
                          : t('{n} steps', { n: test.steps_count })
                        }
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="lexa-suite-primary-action lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                      onClick={() => handleAddTest(test.id, test.type)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {t("Add")}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      )}
  </>);
}
